import { JoinRequest } from '@weblite/ts-action/hall'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Action, MINUTE_MS, useStateRef } from '@weblite/logic-toolkit'
import { useOpenedUserListItemsWithoutReset } from '$logic/opened-user-list/react-hook'
import { JOIN_HALL_REQUESTS_STORAGE_KEY } from '$logic/opened-user-list/storage'
import { getJoinHallRequests } from './other.service'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  AddToOpenedUserList,
  resetOpenedUserList,
} from '$logic/opened-user-list/service'
import { useSearchedUserByIds } from '$logic/users/react-hook'

export const JOIN_HALL_REQUEST_ENABLED =
  import.meta.env.VITE_ENABLE_JOIN_HALL_REQUEST === 'true'

const JoinHallRequestsConfig = {
  pageToken: '',
  roomId: '',
  lastResetDate: 0,
  hasMoreItemToLoad: true,
  totalCount: 0,
  hasMoreItemToLoadChangedAction: new Action<boolean>(),
  totalCountChangedAction: new Action<number>(),

  setHasMoreItemToLoad(hasMoreItemToLoad: boolean) {
    JoinHallRequestsConfig.hasMoreItemToLoad = hasMoreItemToLoad
    JoinHallRequestsConfig.hasMoreItemToLoadChangedAction.invoke(
      hasMoreItemToLoad,
    )
  },

  setTotalCount(totalCount: number) {
    JoinHallRequestsConfig.totalCount = totalCount
    JoinHallRequestsConfig.totalCountChangedAction.invoke(totalCount)
  },

  setPageToken(pageToken: string) {
    JoinHallRequestsConfig.pageToken = pageToken
  },
}

export function useJoinHallRequestsBase({
  hallId,
  query,
  expirationInterval = 10 * MINUTE_MS,
}: {
  hallId: string
  query?: string
  expirationInterval?: number
}) {
  const { items: fetchedItems } = useOpenedUserListItemsWithoutReset(
    JOIN_HALL_REQUESTS_STORAGE_KEY,
  )

  const [hasError, setHasError] = useState(false)

  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(
    JoinHallRequestsConfig.hasMoreItemToLoad,
  )
  const [hasMoreItemToLoadInSearch, setHasMoreItemToLoadInSearch] =
    useState<boolean>(true)

  const [loading, setLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(
    JoinHallRequestsConfig.totalCount,
  )

  const loadingRef = useStateRef<boolean>(loading)

  const searchPageTokenRef = useRef<string>('')

  const hasMoreItemToLoadRef = useStateRef<boolean>(hasMoreItemToLoad)
  const hasMoreItemToLoadInSearchRef = useStateRef<boolean>(
    hasMoreItemToLoadInSearch,
  )

  useEffect(() => {
    const removeHasMoreItemToLoadSubscription =
      JoinHallRequestsConfig.hasMoreItemToLoadChangedAction.add(
        setHasMoreItemToLoad,
      )

    const removeTotalCountSubscription =
      JoinHallRequestsConfig.totalCountChangedAction.add(setTotalCount)

    return () => {
      removeHasMoreItemToLoadSubscription()
      removeTotalCountSubscription()
    }
  }, [])

  const loadMore = useCallback(
    async (force = false) => {
      if (!force && loadingRef.current) return
      if (!hasMoreItemToLoadRef.current) return
      if (query && !hasMoreItemToLoadInSearchRef.current) return

      try {
        setLoading(true)
        setHasError(false)
        const res = await getJoinHallRequests(
          hallId,
          query ? searchPageTokenRef.current : JoinHallRequestsConfig.pageToken,
          query,
        )

        if (!JoinHallRequestsConfig.pageToken)
          JoinHallRequestsConfig.lastResetDate = dateWithOffset()
        JoinHallRequestsConfig.setTotalCount(res.totalCount)
        batch(() => {
          if (!res.pageToken) {
            if (query) setHasMoreItemToLoadInSearch(false)
            else JoinHallRequestsConfig.setHasMoreItemToLoad(false)
          }

          if (query) searchPageTokenRef.current = res.pageToken ?? ''
          else JoinHallRequestsConfig.setPageToken(res.pageToken ?? '')

          AddToOpenedUserList(res.requests, JOIN_HALL_REQUESTS_STORAGE_KEY)
        })
      } catch (error) {
        setHasError(true)
      } finally {
        setLoading(false)
      }
    },
    [
      hallId,
      query,
      hasMoreItemToLoadRef,
      hasMoreItemToLoadInSearchRef,
      loadingRef,
    ],
  )

  const loadMoreFnRef = useStateRef(loadMore)

  useEffect(() => {
    if (
      JoinHallRequestsConfig.roomId !== hallId ||
      dateWithOffset() >
        JoinHallRequestsConfig.lastResetDate + expirationInterval
    ) {
      resetOpenedUserList(JOIN_HALL_REQUESTS_STORAGE_KEY)
      JoinHallRequestsConfig.roomId = hallId
      JoinHallRequestsConfig.setHasMoreItemToLoad(true)
      setHasMoreItemToLoadInSearch(true)
      JoinHallRequestsConfig.setPageToken('')
      searchPageTokenRef.current = ''
    }
    loadMoreFnRef.current(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallId])

  useEffect(() => {
    loadMoreFnRef.current()
    const resetHandler = () => {
      setHasMoreItemToLoadInSearch(true)
      // Note: Server doesn't support this. Server saves query in pageToken and if the query in page token and the request query are not equal,
      // Server ignores the pageToken.
      searchPageTokenRef.current = JoinHallRequestsConfig.pageToken
    }

    return resetHandler
  }, [query, loadMoreFnRef, loadingRef])

  const searchResult = useSearchedUserByIds({
    query: query || '',
    userIds: (fetchedItems as JoinRequest[]).map(item => item.userId),
    searchThreshold: 0,
  })

  const localSearchResult = useMemo(() => {
    return searchResult?.searchedItems?.map(userId =>
      (fetchedItems as JoinRequest[]).find(item => item.userId === userId),
    ) as JoinRequest[]
  }, [searchResult?.searchedItems, fetchedItems])

  const handleRetry = useCallback(() => {
    loadMoreFnRef.current(true)
  }, [loadMoreFnRef])

  return {
    hasError,
    loading,
    items: query ? localSearchResult : (fetchedItems as JoinRequest[]),
    loadMore,
    hasMoreItemToLoad,
    totalCount,
    retry: handleRetry,
  }
}
